import type { Email, EmailFolder } from '@/types';

/**
 * Tipo MIME propio del arrastre de correos. Un archivo o un texto suelto
 * arrastrado sobre una carpeta no lo trae, así que no se confunde con correos.
 */
export const EMAIL_DRAG_TYPE = 'application/x-kila-emails';

export function setDraggedEmails(event: DragEvent, emailIds: string[]): void {
  if (!event.dataTransfer) return;
  event.dataTransfer.setData(EMAIL_DRAG_TYPE, JSON.stringify(emailIds));
  event.dataTransfer.effectAllowed = 'move';
}

export function isEmailDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types || []).includes(EMAIL_DRAG_TYPE);
}

export function getDraggedEmails(event: DragEvent): string[] {
  const raw = event.dataTransfer?.getData(EMAIL_DRAG_TYPE);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Correo que sigue en la bandeja de entrada. La bandeja de Kila lee de
 * Firestore y allí `folder` solo se escribe cuando el correo se mueve desde
 * Kila; ausente significa que nunca salió de la bandeja.
 */
export function isInInbox(email: Pick<Email, 'folder'>): boolean {
  return !email.folder || email.folder === 'inbox';
}

/** Campos del documento de Firestore que deciden la carpeta de un correo. */
export interface FolderDocFields {
  folder?: string | null;
  deletedAt?: unknown;
  folderByUser?: Record<string, { folder?: string } | undefined> | null;
}

/**
 * Carpeta en la que ESTE usuario tiene un correo compartido.
 *
 * El documento es uno por compañía, pero cada persona lo gestiona como en su
 * Outlook/Gmail: el backend guarda la carpeta de cada quien en `folderByUser`
 * (clave = id de la conexión de correo). La papelera sigue siendo compartida
 * y manda sobre lo demás. `folder` suelto es el valor heredado de antes de
 * este cambio y aplica a todos.
 */
export function resolveUserFolder(doc: FolderDocFields, mailboxUserId?: string | null): string | undefined {
  if (doc.folder === 'trash' || doc.deletedAt) return 'trash';
  const own = mailboxUserId ? doc.folderByUser?.[mailboxUserId]?.folder : undefined;
  if (own) return own;
  return doc.folder || undefined;
}

export interface FolderTreeNode {
  folder: EmailFolder;
  depth: number;
}

const MAX_TREE_DEPTH = 5;

/**
 * Aplana las carpetas en orden de árbol: cada padre seguido de sus hijas,
 * alfabético por nivel. Una carpeta cuyo padre no está en la lista se dibuja
 * en la raíz en vez de desaparecer.
 */
export function buildFolderTree(folders: EmailFolder[]): FolderTreeNode[] {
  const ids = new Set(folders.map((folder) => folder.id));
  const byParent = new Map<string, EmailFolder[]>();

  for (const folder of folders) {
    const parent = folder.parentFolderId && ids.has(folder.parentFolderId) ? folder.parentFolderId : '';
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent)!.push(folder);
  }

  const byName = (a: EmailFolder, b: EmailFolder) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });

  const result: FolderTreeNode[] = [];
  const visit = (parent: string, depth: number) => {
    for (const folder of (byParent.get(parent) || []).sort(byName)) {
      result.push({ folder, depth });
      if (depth < MAX_TREE_DEPTH) visit(folder.id, depth + 1);
    }
  };
  visit('', 0);
  return result;
}

/** Texto comparable: sin mayúsculas ni tildes, para buscar "polizas" y hallar "PÓLIZAS". */
function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Filtra el árbol por nombre conservando la jerarquía: una carpeta se queda si
 * coincide ella, un ancestro suyo o una descendiente. Así "SKY" no aparece
 * huérfana cuando su padre "PROYECTOS" no coincide.
 */
export function filterFolderTree(tree: FolderTreeNode[], query: string): FolderTreeNode[] {
  const needle = normalize(query.trim());
  if (!needle) return tree;

  const matches = new Set<string>();
  // Ancestros del nodo actual, por nivel: el que está en depth - 1 es su padre.
  const ancestors: FolderTreeNode[] = [];
  for (const node of tree) {
    ancestors.length = node.depth;
    ancestors[node.depth] = node;
    if (!normalize(node.folder.displayName).includes(needle)) continue;
    for (const ancestor of ancestors) matches.add(ancestor.folder.id);
  }

  const result: FolderTreeNode[] = [];
  let keptDepth = -1;
  for (const node of tree) {
    // Una descendiente de algo que coincidió se queda aunque ella no coincida.
    if (keptDepth >= 0 && node.depth > keptDepth) {
      result.push(node);
      continue;
    }
    keptDepth = -1;
    if (!matches.has(node.folder.id)) continue;
    result.push(node);
    if (normalize(node.folder.displayName).includes(needle)) keptDepth = node.depth;
  }
  return result;
}

export interface FolderMatch {
  folder: EmailFolder;
  /** Ruta de los padres, "PROYECTOS / SKY"; vacía si la carpeta está en la raíz. */
  path: string;
}

/**
 * Coincidencias en lista plana, con la ruta del padre para distinguir dos
 * carpetas que se llaman igual en ramas distintas.
 */
export function searchFolders(folders: EmailFolder[], query: string): FolderMatch[] {
  const needle = normalize(query.trim());
  if (!needle) return [];

  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const pathOf = (folder: EmailFolder): string => {
    const names: string[] = [];
    let parent = folder.parentFolderId ? byId.get(folder.parentFolderId) : undefined;
    while (parent && names.length < MAX_TREE_DEPTH) {
      names.unshift(parent.displayName);
      parent = parent.parentFolderId ? byId.get(parent.parentFolderId) : undefined;
    }
    return names.join(' / ');
  };

  return folders
    .filter((folder) => normalize(folder.displayName).includes(needle))
    .sort((a, b) => normalize(a.displayName).localeCompare(normalize(b.displayName)))
    .map((folder) => ({ folder, path: pathOf(folder) }));
}

/** Máximo de carpetas recordadas en "Más recientes". */
export const MAX_RECENT_FOLDERS = 5;

/** Deja la carpeta recién usada de primera, sin repetirla y sin pasar del tope. */
export function addRecentFolder(recent: string[], folderId: string): string[] {
  return [folderId, ...recent.filter((id) => id !== folderId)].slice(0, MAX_RECENT_FOLDERS);
}
